import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_SUPABASE_KEY;
const tenantId = import.meta.env.VITE_TENANT_ID;

if (!supabaseUrl || !supabaseKey) {
  throw new Error('Missing Supabase environment variables');
}

if (!tenantId) {
  console.warn('VITE_TENANT_ID is not defined. Using default tenant_id=1');
}

// Créer le client Supabase avec des headers personnalisés pour le multi-tenant
export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    // Utiliser 'implicit' en développement pour éviter les problèmes de PKCE
    // En production, PKCE est recommandé pour plus de sécurité
    flowType: import.meta.env.DEV ? 'implicit' : 'pkce',
    // Augmenter la durée avant expiration du token
    storage: window.localStorage,
    storageKey: 'supabase.auth.token'
  },
  db: {
    schema: 'public'
  },
  global: {
    headers: {
      'x-tenant-id': tenantId || '1'
    }
  }
});

// Écouter les changements d'état d'authentification pour gérer les sessions expirées
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('Token rafraîchi automatiquement');
  } else if (event === 'SIGNED_OUT') {
    console.log('Utilisateur déconnecté');
  } else if (event === 'USER_UPDATED') {
    console.log('Utilisateur mis à jour');
  }
});

// Intercepter toutes les requêtes pour ajouter le tenant_id
const { insert, update, upsert } = supabase.from('').constructor.prototype;

// Surcharge de la méthode insert pour ajouter automatiquement tenant_id
supabase.from('').constructor.prototype.insert = function(values, options) {
  // Si c'est un tableau d'objets
  if (Array.isArray(values)) {
    values = values.map(row => ({ ...row, tenant_id: parseInt(tenantId || '1') }));
  } 
  // Si c'est un objet unique
  else if (values && typeof values === 'object') {
    values = { ...values, tenant_id: parseInt(tenantId || '1') };
  }
  return insert.call(this, values, options);
};

// Surcharge de la méthode update pour ajouter automatiquement tenant_id
supabase.from('').constructor.prototype.update = function(values, options) {
  if (values && typeof values === 'object') {
    values = { ...values, tenant_id: parseInt(tenantId || '1') };
  }
  return update.call(this, values, options);
};

// Surcharge de la méthode upsert pour ajouter automatiquement tenant_id
supabase.from('').constructor.prototype.upsert = function(values, options) {
  // Si c'est un tableau d'objets
  if (Array.isArray(values)) {
    values = values.map(row => ({ ...row, tenant_id: parseInt(tenantId || '1') }));
  } 
  // Si c'est un objet unique
  else if (values && typeof values === 'object') {
    values = { ...values, tenant_id: parseInt(tenantId || '1') };
  }
  return upsert.call(this, values, options);
};

export const signIn = async (email: string, password: string) => {
  try {
    // Vérifier d'abord si l'utilisateur est actif avant de l'authentifier
    const { data: userByEmail, error: userByEmailError } = await supabase
      .from('users')
      .select('is_active')
      .eq('email', email)
      .single();
    
    // Si l'utilisateur existe et est inactif, bloquer immédiatement
    if (userByEmail && userByEmail.is_active === false) {
      console.log('Tentative de connexion avec un compte inactif:', email);
      throw new Error('Votre compte est inactif. Veuillez contacter l\'administrateur.');
    }

    // Procéder à l'authentification seulement si le compte est actif ou n'existe pas encore
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Sign in error:', error);
      throw error;
    }
    
    // Double vérification après authentification (au cas où)
    if (data.user) {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('is_active')
        .eq('id', data.user.id)
        .single();
      
      if (userError) {
        console.error('User data fetch error:', userError);
        // Déconnecter l'utilisateur si on ne peut pas vérifier son statut
        await supabase.auth.signOut();
        throw new Error('Impossible de vérifier le statut du compte');
      }
      
      if (userData && userData.is_active === false) {
        // Déconnecter l'utilisateur s'il est inactif
        await supabase.auth.signOut();
        throw new Error('Votre compte est inactif. Veuillez contacter l\'administrateur.');
      }
    }

    return { data, error: null };
  } catch (error: any) {
    console.error('Sign in catch error:', error);
    return {
      data: null,
      error: {
        message: error.message || 'Une erreur est survenue lors de la connexion'
      }
    };
  }
};

export const signOut = async () => {
  try {
    const { error } = await supabase.auth.signOut();
    if (error) {
      console.error('Sign out error:', error);
      throw error;
    }
    return { error: null };
  } catch (error: any) {
    console.error('Sign out catch error:', error);
    return {
      error: {
        message: error.message || 'Une erreur est survenue lors de la déconnexion'
      }
    };
  }
};

export const getSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      console.error('Get session error:', error);
      throw error;
    }
    return { session, error: null };
  } catch (error: any) {
    console.error('Get session catch error:', error);
    return {
      session: null,
      error: {
        message: error.message || 'Une erreur est survenue lors de la récupération de la session'
      }
    };
  }
};
